import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Sparkles, Share2, Bell, Trash2, TrendingUp, Shield, Mail } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    name: '',
    mood: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    notification_time: '9:00',
    newsletter_subscribed: false
  });
  const [userSessionId] = useState(() => {
    return localStorage.getItem('user_session_id') || '';
  });
  const [userEmail, setUserEmail] = useState('');
  const [showInvite, setShowInvite] = useState(false);

  const moods = [
    { value: 'calm', label: 'Calm', emoji: '🕊️' },
    { value: 'heavy', label: 'Heavy', emoji: '🌧️' },
    { value: 'healing', label: 'Healing', emoji: '🌱' },
    { value: 'rising', label: 'Rising', emoji: '✨' }
  ];

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      // Load from localStorage
      const savedProfile = localStorage.getItem('user_profile');
      if (savedProfile) {
        const profileData = JSON.parse(savedProfile);
        setProfile(profileData);
        setUserEmail(profileData.email || '');
      }
      
      // Check newsletter subscription status
      if (userEmail) {
        try {
          const response = await axios.get(`${API}/newsletter/check/${encodeURIComponent(userEmail)}`);
          setProfile(prev => ({ ...prev, newsletter_subscribed: response.data.subscribed }));
        } catch (error) {
          console.error('Error checking newsletter:', error);
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const saveProfile = async () => {
    localStorage.setItem('user_profile', JSON.stringify(profile));
    
    // Handle newsletter subscription
    if (userEmail) {
      try {
        if (profile.newsletter_subscribed) {
          await axios.post(`${API}/newsletter/subscribe`, {
            email: userEmail,
            source: 'profile',
            user_session_id: userSessionId
          });
        } else {
          await axios.delete(`${API}/newsletter/unsubscribe/${encodeURIComponent(userEmail)}`);
        }
      } catch (error) {
        console.error('Newsletter update error:', error);
      }
    }
    
    toast.success('Your space is updated 💜');
  };

  const handleInvite = async (platform) => {
    const shareText = `Hey love, I found this app called SheSafe Haven. It's a calm space filled with daily whispers that helped me breathe again.\nThought it might bring you a bit of light too. 💜\n\n${window.location.origin}`;
    
    if (platform === 'whatsapp') {
      // WhatsApp share
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
      window.open(whatsappUrl, '_blank');
      toast.success('Opening WhatsApp... 💜');
    } else if (platform === 'native' && navigator.share) {
      // Native share for other options
      try {
        await navigator.share({
          title: 'SheSafe Haven',
          text: shareText
        });
        toast.success('Thank you for sharing healing 💜');
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyInviteLink(shareText);
        }
      }
    } else {
      // Fallback to clipboard
      copyInviteLink(shareText);
    }
  };

  const copyInviteLink = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Message copied! Share it with someone who needs it 💜');
  };

  const clearJourney = () => {
    if (window.confirm('This will clear your journey, journal entries, and favorites. Your daily progress will start fresh. Are you sure?')) {
      // Clear localStorage
      localStorage.removeItem('user_profile');
      localStorage.removeItem('user_session_id');
      
      toast.success('Your space has been cleared. Begin again, whenever you\'re ready.');
      
      // Refresh page to reset
      setTimeout(() => window.location.reload(), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-[#2E2536]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#2E2536]/95 backdrop-blur-sm border-b border-[#C8B5E0]/20">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="icon"
              className="text-[#EDE8F3] hover:bg-[#C8B5E0]/20 rounded-full"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            
            <div className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-[#E7C6C2]" />
              <h1 className="text-2xl font-bold text-[#EDE8F3]">My Haven</h1>
            </div>
            
            <div className="w-10"></div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Intro */}
        <div className="text-center mb-10">
          <p className="text-[#C8B5E0] leading-relaxed">
            This is your quiet corner. No judgment, no expectations — just a place to return to yourself.
          </p>
        </div>

        {/* Profile Form */}
        <div className="space-y-6">
          {/* Name */}
          <div className="bg-[#3E324D] rounded-xl p-6">
            <label className="block text-[#EDE8F3] mb-2">What would you like to be called here?</label>
            <input
              type="text"
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              placeholder="Your name or nickname..."
              className="w-full bg-[#2E2536] text-[#EDE8F3] placeholder-[#6E6275] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C8B5E0]"
            />
          </div>

          {/* Mood */}
          <div className="bg-[#3E324D] rounded-xl p-6">
            <label className="block text-[#EDE8F3] mb-4">How's your heart feeling?</label>
            <div className="grid grid-cols-2 gap-3">
              {moods.map((mood) => (
                <button
                  key={mood.value}
                  onClick={() => setProfile({ ...profile, mood: mood.value })}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    profile.mood === mood.value
                      ? 'border-[#E7C6C2] bg-[#E7C6C2]/20'
                      : 'border-[#C8B5E0]/20 hover:border-[#C8B5E0]/40'
                  }`}
                >
                  <div className="text-3xl mb-2">{mood.emoji}</div>
                  <div className="text-[#EDE8F3] font-medium">{mood.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Notification Time */}
          <div className="bg-[#3E324D] rounded-xl p-6">
            <label className="block text-[#EDE8F3] mb-2">
              <Bell className="w-4 h-4 inline mr-2" />
              When would you like your daily whisper?
            </label>
            <p className="text-sm text-[#C8B5E0] mb-4">So we can send it at the right moment.</p>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => setProfile({ ...profile, notification_time: '7:00' })}
                className={`p-3 rounded-lg border-2 ${
                  profile.notification_time === '7:00'
                    ? 'border-[#E7C6C2] bg-[#E7C6C2]/20'
                    : 'border-[#C8B5E0]/20'
                }`}
              >
                <div className="text-2xl mb-1">🌅</div>
                <div className="text-[#EDE8F3] text-sm">Morning</div>
                <div className="text-[#C8B5E0] text-xs">7:00 AM</div>
              </button>
              <button
                onClick={() => setProfile({ ...profile, notification_time: '12:00' })}
                className={`p-3 rounded-lg border-2 ${
                  profile.notification_time === '12:00'
                    ? 'border-[#E7C6C2] bg-[#E7C6C2]/20'
                    : 'border-[#C8B5E0]/20'
                }`}
              >
                <div className="text-2xl mb-1">☀️</div>
                <div className="text-[#EDE8F3] text-sm">Noon</div>
                <div className="text-[#C8B5E0] text-xs">12:00 PM</div>
              </button>
              <button
                onClick={() => setProfile({ ...profile, notification_time: '20:00' })}
                className={`p-3 rounded-lg border-2 ${
                  profile.notification_time === '20:00'
                    ? 'border-[#E7C6C2] bg-[#E7C6C2]/20'
                    : 'border-[#C8B5E0]/20'
                }`}
              >
                <div className="text-2xl mb-1">🌙</div>
                <div className="text-[#EDE8F3] text-sm">Evening</div>
                <div className="text-[#C8B5E0] text-xs">8:00 PM</div>
              </button>
            </div>
          </div>

          {/* Newsletter Subscription Toggle */}
          <div className="bg-[#3E324D] rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <label className="block text-[#EDE8F3] mb-2 font-semibold">
                  Daily Whispers via Email 💜
                </label>
                <p className="text-sm text-[#C8B5E0] mb-4">
                  Receive gentle healing messages and reminders in your inbox
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={profile.newsletter_subscribed}
                  onChange={(e) => setProfile({ ...profile, newsletter_subscribed: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-[#2E2536] peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#E7C6C2] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#E7C6C2]"></div>
              </label>
            </div>
          </div>

          {/* Save Button */}
          <Button
            onClick={saveProfile}
            className="w-full bg-[#6C4CA2] hover:bg-[#8B6BB8] text-white py-4 rounded-full text-lg"
          >
            Save My Haven
          </Button>
        </div>

        {/* Divider */}
        <div className="my-10 border-t border-[#C8B5E0]/20"></div>

        {/* Safety Features */}
        <div className="bg-[#3E324D] rounded-xl p-6 mb-6 border-2 border-[#E7C6C2]/30">
          <h3 className="text-xl font-semibold text-[#EDE8F3] mb-3 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#E7C6C2]" />
            Safety Features
          </h3>
          <p className="text-[#C8B5E0] mb-4 leading-relaxed text-sm">
            Learn about the emergency exit feature and other safety tools designed to protect you
          </p>
          <Button
            onClick={() => navigate('/safety-features')}
            className="w-full bg-[#E7C6C2] hover:bg-[#F7E5E5] text-[#3E324D] py-3 rounded-full"
          >
            View Safety Features
          </Button>
        </div>

        {/* Your Insights */}
        <div className="bg-[#3E324D] rounded-xl p-6 mb-6">
          <h3 className="text-xl font-semibold text-[#EDE8F3] mb-3 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#E7C6C2]" />
            Your Healing Insights
          </h3>
          <p className="text-[#C8B5E0] mb-4 leading-relaxed text-sm">
            See how the app learns from your patterns to support you better
          </p>
          <Button
            onClick={() => navigate('/insights')}
            className="w-full bg-[#6C4CA2] hover:bg-[#8B6BB8] text-white py-3 rounded-full"
          >
            View Your Insights
          </Button>
        </div>

        {/* Invite a Sister */}
        <div className="bg-[#3E324D] rounded-xl p-6 mb-6">
          <h3 className="text-xl font-semibold text-[#EDE8F3] mb-3 flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[#E7C6C2]" />
            Invite a Sister
          </h3>
          <p className="text-[#C8B5E0] mb-4 leading-relaxed">
            Someone out there needs to hear what helped you. Share SheSafe Haven — a gentle space for healing, clarity, and calm.
          </p>
          <div className="flex gap-3">
            <Button
              onClick={() => handleInvite('whatsapp')}
              className="flex-1 bg-[#25D366] hover:opacity-90 text-white py-3 rounded-full flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              WhatsApp
            </Button>
            <Button
              onClick={() => handleInvite('native')}
              className="flex-1 bg-[#E7C6C2] hover:bg-[#F7E5E5] text-[#3E324D] py-3 rounded-full flex items-center justify-center gap-2"
            >
              <Share2 className="w-5 h-5" />
              More Options
            </Button>
          </div>
        </div>

        {/* Privacy */}
        <div className="bg-[#3E324D] rounded-xl p-6">
          <h3 className="text-lg font-semibold text-[#EDE8F3] mb-3">Privacy</h3>
          <p className="text-[#C8B5E0] mb-4 text-sm">
            You can clear your journey anytime. Your story is yours alone.
          </p>
          <Button
            onClick={clearJourney}
            variant="ghost"
            className="w-full text-red-400 hover:text-red-300 hover:bg-red-400/10"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear My Journey
          </Button>
        </div>

        {/* Logout */}
        <div className="mt-8 text-center">
          <Button
            onClick={() => {
              localStorage.clear();
              navigate('/');
              window.location.reload();
            }}
            variant="ghost"
            className="text-[#C8B5E0] hover:text-[#EDE8F3]"
          >
            Step away with peace — your space will be here when you return
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
